import { useEffect, useRef, useState } from 'react'
import { Divider, EmptyPlaceholder, WizardFooter, WizardTitle } from "@components"
import { getSessionDateRange } from "@helpers"
import { Button, Checkbox, CheckboxGroup, Chip } from "@heroui/react"
import { Square3Stack3DIcon } from "@heroicons/react/24/outline"
import { orderBy } from "lodash"
import PropTypes from 'prop-types'

const Step2 = ({ 
  currentSelectedGroupIds, 
  handlePrevious, 
  handleNext, 
  isSubmitting, 
  sessions 
}) => {
  const [selectedGroupIds, setSelectedGroupIds] = useState([])
  const sessionRefs = useRef({})

  useEffect(() => {
    setSelectedGroupIds(currentSelectedGroupIds)
  }, [currentSelectedGroupIds])

  const handleClearSessionGroups = (session) => {
    const sessionGroupIds = session.groups.map(group => group.id)
    const updatedSelectedGroupIds = selectedGroupIds.filter(id => !sessionGroupIds.includes(id))
    setSelectedGroupIds(updatedSelectedGroupIds)
  }

  const handleSelectSessionGroups = (session) => {
    const sessionGroupIds = session.groups.map(group => group.id)
    setSelectedGroupIds(Array.from(new Set([...selectedGroupIds, ...sessionGroupIds])))
  }

  const handleUpdateSelectedGroups = (value) => {
    setSelectedGroupIds(value)
  }

  const scrollToSession = (sessionId) => {
    sessionRefs.current[sessionId]?.scrollIntoView({ behavior: 'smooth' })
  }

  const noOfSelected = selectedGroupIds.length
  const noOfDeselected = sessions.reduce((acc, session) => {
    return acc + session.groups.filter(group => !selectedGroupIds.includes(group.id)).length
  }, 0)
  const noOfGroups = noOfDeselected + noOfSelected
  
  return (
    <div className="max-h-[65vh]">
      <WizardTitle 
        title="Step 2 of 4 - Select Groups"
        description="Select the groups that will receive this communication."
        icon={<Square3Stack3DIcon />}
        titleAddon={
          <Chip className={`font-semibold text-white ${noOfSelected === noOfGroups ? 'bg-green-500' : 'bg-orange-500'}`}>
            {noOfSelected} of {noOfGroups} Selected
          </Chip>
        }
      />

      {sessions.length > 1 && (
        <div>
          {sessions.map((session) => {
            const noOfSelected = session.groups.filter(group => selectedGroupIds.includes(group.id)).length
            const noOfGroups = session.groups.length
            
            return (
              <div key={session.id}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <Square3Stack3DIcon className="w-6 h-6 mr-2 text-content2-foreground" />
                    
                    <button 
                      className="mr-2 font-semibold text-foreground" 
                      onClick={() => scrollToSession(session.id)}
                    >
                      View {session.title}
                    </button>
                  </div>
                  
                  <Chip className={`min-w-40 text-center font-semibold text-white ${noOfSelected === noOfGroups ? 'bg-green-500' : 'bg-orange-500'}`}>
                    {noOfSelected} of {noOfGroups} Selected
                  </Chip>
                </div>
                
                <Divider className="my-2" />
              </div>
            )
          })}
        </div>
      )}

      <div className="flex flex-col mx-1 mt-4 space-y-4">
        {orderBy(sessions, ["title"]).map((session) => {
          const noOfSelected = session.groups.filter(group => selectedGroupIds.includes(group.id)).length
          const noOfDeselected = session.groups.length - noOfSelected

          return (
            <div id={`session_${session.id}`} className="rounded-lg bg-content2" key={session.id} ref={el => sessionRefs.current[session.id] = el}>
              <div className="flex items-center justify-between px-4 py-2">
                <div>
                  <h1 className="text-lg font-semibold">{session.title}</h1>
                  <p>{getSessionDateRange(session.starts_at, session.ends_at)}</p>
                </div>

                <div className="flex items-center gap-2">
                  <Button onPress={() => handleSelectSessionGroups(session)}>Select All ({ noOfDeselected })</Button>
                  <Button onPress={() => handleClearSessionGroups(session)}>Clear All ({ noOfSelected })</Button>
                </div>
              </div>
              
              {session.groups.length === 0 && (
                <EmptyPlaceholder classNames="mt-2 mx-4" title="No groups found" description="There are no groups available for this session." />
              )}

              <div className="p-3 rounded-b-lg bg-content2">
                <CheckboxGroup size="md" value={selectedGroupIds} onValueChange={(value) => handleUpdateSelectedGroups(value)}>
                  {orderBy(session.groups, ["title"]).map((group) => (
                    <div key={group.id}>
                      <Checkbox value={group.id}>{group.title}</Checkbox>
                    </div>
                  ))}
                </CheckboxGroup>
              </div>
            </div>
          )
        })}
      </div>

      <div className="sticky z-50 pb-2 bg-content1 -bottom-2">
        <WizardFooter 
          currentStep={2} 
          totalSteps={5} 
          previousCaption="Notification Setup" 
          nextCaption="Select Recipients" 
          handlePrevious={() => handlePrevious(selectedGroupIds)} 
          handleNext={() => handleNext(selectedGroupIds)} 
          isSubmitting={isSubmitting}
          isNextButtonDisabled={selectedGroupIds.length === 0}
        />
      </div>
    </div>
  )
}

Step2.propTypes = {
  currentSelectedGroupIds: PropTypes.array.isRequired,
  handlePrevious: PropTypes.func.isRequired,
  handleNext: PropTypes.func.isRequired,
  isSubmitting: PropTypes.bool.isRequired,
  sessions: PropTypes.array.isRequired,
}

export default Step2